// src/pages/Profile/Profile.jsx
import React, { useEffect, useRef, useState } from "react";
import Button from "./../../components/Button/Button";
import "react-toastify/dist/ReactToastify.css";
import backgroundImage from "./HeaderSlider_02.jpg";
import { ToastContainer, toast } from "react-toastify";
import useScrollToTop from "./../../hooks/useScollToTop";
import {
  getUserById,
  updateUserProfile,
  toggleEmailNotifications,
} from "./../../services/userServices";
import {
  FaSave,
  FaTimes,
  FaCloudUploadAlt,
  FaLink,
  FaImage,
  FaSyncAlt,
} from "react-icons/fa";
import { getRoleFromRoleId } from "./../../utils/roleHelper";

// Services photo USER (mêmes signatures/patterns que adhérents)
import {
  uploadUserPhoto,
  cropUserPhoto,
  toAbsoluteUserPhotoUrl,
} from "./../../services/userPhotoServices";
import CropPhotoModal, {
  toMasterUrl,
} from "./../../components/CropPhotoModal/CropPhotoModal";

const DEFAULT_AVATAR = "/img/user_avatar.jpg";

const ROLE_META = {
  1: {
    label: "Administrateur",
    className: "bg-red-100 text-red-700 border border-red-200",
  },
  2: {
    label: "Utilisateur",
    className: "bg-blue-100 text-blue-700 border border-blue-200",
  },
  3: {
    label: "Enseignant",
    className: "bg-green-100 text-green-700 border border-green-200",
  },
};

const withBust = (u, v) =>
  !u ? u : u.includes("?") ? `${u}&v=${v}` : `${u}?v=${v}`;

const Profile = () => {
  useScrollToTop();

  const [user, setUser] = useState({
    id: "",
    nom: "",
    prenom: "",
    email: "",
    photoURL: "",
    role_id: 2,
    email_notifications_enabled: false,
  });

  const userRole = getRoleFromRoleId(user?.role_id);

  const [isEditing, setIsEditing] = useState(false);

  // —— Gestion modale photo (pattern identique à EditAdherentAdminModal) ——
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [uploadMode, setUploadMode] = useState("url"); // 'url' | 'upload'
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [outputFormat, setOutputFormat] = useState("webp"); // 'webp' | 'jpg' | 'png'
  const fileInputRef = useRef(null);
  const [imgVersion, setImgVersion] = useState(0); // anti-cache preview
  const [imgOk, setImgOk] = useState(true);
  const [isCropOpen, setIsCropOpen] = useState(false);
  const [isRefreshingRole, setIsRefreshingRole] = useState(false);

  const refreshUserProfile = async (showToastMessage = false) => {
    const ls = localStorage.getItem("userInfo");
    const userInfo = ls ? JSON.parse(ls) : null;
    const userId = userInfo?.userId;

    if (!userId) return;

    try {
      const userData = await getUserById(userId);

      setUser((prev) => ({
        ...prev,
        ...userData,
        email_notifications_enabled: !!userData.email_notifications_enabled,
      }));

      setImgOk(true);
      setImgVersion((v) => v + 1);

      // Mise à jour localStorage pour que le rôle soit bien réutilisé partout
      const currentInfo = localStorage.getItem("userInfo");
      const stored = currentInfo ? JSON.parse(currentInfo) : {};

      localStorage.setItem(
        "userInfo",
        JSON.stringify({
          ...stored,
          nom: userData.nom ?? stored.nom,
          prenom: userData.prenom ?? stored.prenom,
          photoURL: userData.photoURL ?? stored.photoURL,
          role_id: userData.role_id ?? stored.role_id,
          userId: stored.userId ?? userData.id,
        }),
      );

      if (showToastMessage) {
        toast.success("Profil et rôle actualisés !");
      }
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des données de l'utilisateur:",
        error,
      );
      toast.error("Erreur lors de l’actualisation du profil.");
    }
  };

  useEffect(() => {
    refreshUserProfile();
  }, []);

  const handleEdit = () => setIsEditing(true);
  const handleCloseModal = () => {
    setIsEditing(false);
    setIsPhotoModalOpen(false);
    setIsUploading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const updated = await updateUserProfile(user.id, user, token);
      // Maj localStorage (utile pour header/avatar global)
      const currentInfo = localStorage.getItem("userInfo");
      const stored = currentInfo ? JSON.parse(currentInfo) : {};
      localStorage.setItem(
        "userInfo",
        JSON.stringify({
          ...stored,
          nom: updated.nom,
          prenom: updated.prenom,
          photoURL: updated.photoURL || stored.photoURL,
          role_id: updated.role_id ?? stored.role_id,
          userId: stored.userId,
        }),
      );
      setUser((p) => ({ ...p, ...updated }));
      setIsEditing(false);
      toast.success("Profil mis à jour avec succès!");
    } catch (error) {
      console.error(
        "Erreur lors de la mise à jour des données de l'utilisateur:",
        error,
      );
      toast.error(
        "Erreur lors de la mise à jour des données de l'utilisateur.",
      );
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUser((p) => ({ ...p, [name]: value }));
  };

  const openPhotoModal = () => {
    setIsPhotoModalOpen(true);
    setUploadMode("url");
    setUploadProgress(0);
    setIsUploading(false);
    setImgOk(true);
    // on ne touche pas à imgVersion ici, pour garder l'aperçu actuel
  };

  const openFilePicker = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    e.stopPropagation();
    const file = e.target.files?.[0];
    if (!file) return;
    if (!user?.id) {
      toast.error("ID utilisateur manquant.");
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);

      const res = await uploadUserPhoto(user.id, file, {
        format: outputFormat,
        onUploadProgress: (pe) => {
          if (!pe.total) return;
          const percent = Math.round((pe.loaded * 100) / pe.total);
          setUploadProgress(percent);
        },
      });

      const newThumbUrl =
        res?.photo_url ||
        res?.photoURL ||
        res?.imageUrl ||
        res?.url ||
        res?.path ||
        res?.data?.photo_url ||
        res?.data?.photoURL ||
        res?.data?.imageUrl ||
        res?.data?.url ||
        res?.data?.path;

      if (newThumbUrl) {
        // Mettre l'URL dans l'état + forcer refresh
        setUser((p) => ({ ...p, photoURL: newThumbUrl }));
        setImgOk(true);
        setImgVersion((v) => v + 1);

        // Mettre à jour localStorage.userInfo pour la navbar, etc.
        const currentInfo = localStorage.getItem("userInfo");
        const stored = currentInfo ? JSON.parse(currentInfo) : {};
        localStorage.setItem(
          "userInfo",
          JSON.stringify({
            ...stored,
            photoURL: newThumbUrl,
          }),
        );

        toast.success("Photo uploadée et traitée (400×400).");
      } else {
        console.warn("Réponse upload inattendue:", res);
        toast.warn("Upload terminé mais URL non renvoyée.");
      }

      // Autoriser le re-choix du même fichier
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      console.error(err);
      toast.error(`Erreur upload: ${err?.message || "inconnue"}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handlePhotoSave = async (e) => {
    e.preventDefault();
    // En mode URL, on pousse la valeur courante vers l'API profil
    if (uploadMode === "url") {
      try {
        const token = localStorage.getItem("token");
        const updated = await updateUserProfile(user.id, { ...user }, token);
        const currentInfo = localStorage.getItem("userInfo");
        const stored = currentInfo ? JSON.parse(currentInfo) : {};
        localStorage.setItem(
          "userInfo",
          JSON.stringify({
            ...stored,
            photoURL: updated.photoURL || user.photoURL,
          }),
        );
        setUser((p) => ({ ...p, photoURL: updated.photoURL || p.photoURL }));
        setImgVersion((v) => v + 1);
        toast.success("Photo de profil mise à jour !");
        setIsPhotoModalOpen(false);
      } catch (error) {
        console.error(error);
        toast.error("Erreur lors de l’enregistrement de la photo.");
      }
    } else {
      // En mode upload, la mise à jour est déjà faite au moment du choix du fichier
      setIsPhotoModalOpen(false);
    }
  };

  const handleToggleEmailNotifications = async () => {
    const newValue = !user.email_notifications_enabled;
    try {
      const token = localStorage.getItem("token");
      const updatedUser = await toggleEmailNotifications(
        user.id,
        newValue,
        token,
      );
      setUser((prev) => ({
        ...prev,
        email_notifications_enabled: updatedUser.email_notifications_enabled,
      }));
      toast.success(
        `Notifications par mail ${newValue ? "activées" : "désactivées"} !`,
      );
    } catch (error) {
      toast.error("Erreur lors de la mise à jour de la préférence mail.");
    }
  };

  const handleRefreshRole = async () => {
    try {
      setIsRefreshingRole(true);
      await refreshUserProfile(true);

      // Si ton app lit le rôle uniquement au chargement initial,
      // cette ligne permet d’actualiser toute l’interface sans déconnexion :
      window.location.reload();
    } catch (error) {
      console.error(error);
      toast.error("Impossible de rafraîchir le rôle.");
    } finally {
      setIsRefreshingRole(false);
    }
  };

  const absolutePhoto = toAbsoluteUserPhotoUrl(user.photoURL);
  const previewSrc = toAbsoluteUserPhotoUrl(user.photoURL)
    ? withBust(absolutePhoto, imgVersion)
    : withBust(DEFAULT_AVATAR, imgVersion);

  return (
    <div
      className="min-h-screen flex items-center justify-center pt-[150px]"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
      }}
    >
      <div className="bg-gray-100/95 dark:bg-slate-700/60 p-8 rounded-lg shadow-lg w-full max-w-md relative">
        <h1 className="text-2xl font-semibold mb-4 text-center">
          Profil Utilisateur
        </h1>

        <div className="flex flex-col items-center mb-4">
          <img
            key={imgVersion} // remount pour briser le cache
            src={previewSrc}
            alt="Avatar de l'utilisateur"
            className="w-24 h-24 rounded-full object-cover mb-4 border-2 border-slate-400 shadow-lg bg-white"
            onError={() => setImgOk(false)}
          />
          <div className="flex flex-col items-center">
            <Button
              text="Modifier Photo"
              onClick={openPhotoModal}
              className="mb-2"
            />
            {!imgOk && (
              <div className="text-xs text-rose-600 -mt-1">
                Impossible de charger l’image.
              </div>
            )}
          </div>

          <h2 className="text-xl mt-2">
            {user.prenom} {user.nom}
          </h2>

          <div className="mt-2 flex flex-col items-center gap-2">
            <span
              className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                ROLE_META[user.role_id]?.className ||
                "bg-slate-100 text-slate-700 border border-slate-200"
              }`}
            >
              {ROLE_META[user.role_id]?.label ||
                `Rôle inconnu (${user.role_id})`}
            </span>

            <p className="text-gray-600">{user.email}</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-center items-center gap-2">
          <Button text="Modifier les informations" onClick={handleEdit} />
          <Button
            text={isRefreshingRole ? "Actualisation..." : "Rafraîchir le rôle"}
            onClick={handleRefreshRole}
            icon={FaSyncAlt}
            className="gap-2"
          />
        </div>

        {(userRole === "admin" || userRole === "enseignant") && (
          <div className="flex flex-col items-center justify-center mt-4 text-center">
            <div className="flex items-center">
              <label
                htmlFor="email-notifications-switch"
                className="text-gray-700 mr-4"
              >
                📩 Notifications par mail
              </label>
              <input
                id="email-notifications-switch"
                type="checkbox"
                checked={!!user.email_notifications_enabled}
                onChange={handleToggleEmailNotifications}
                className="form-checkbox h-5 w-5 text-blue-600"
              />
            </div>
            <p className="mt-2 text-sm text-gray-600 max-w-md">
              Si vous activez cette option, vous recevrez certaines
              notifications par email.
              <br className="hidden sm:inline" />
              ⚠️ Pensez à vérifier votre dossier spam et à cliquer sur « ceci
              n’est pas un spam » pour recevoir correctement les prochaines
              notifications dans votre boîte de réception.
            </p>
          </div>
        )}

        {/* Modale édition infos */}
        {isEditing && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-40">
            <div className="bg-slate-200 dark:bg-slate-400 dark:text-black p-6 rounded-lg shadow-xl max-w-lg w-full">
              <h2 className="text-xl font-bold uppercase p-3 mb-4 text-center text-white bg-primary rounded-md shadow">
                Modifier le Profil
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label>Nom</label>
                  <input
                    type="text"
                    name="nom"
                    value={user.nom}
                    onChange={handleChange}
                    className="border rounded p-2 w-full"
                  />
                </div>
                <div>
                  <label>Prénom</label>
                  <input
                    type="text"
                    name="prenom"
                    value={user.prenom}
                    onChange={handleChange}
                    className="border rounded p-2 w-full"
                  />
                </div>

                <div className="flex justify-center space-x-2">
                  <Button
                    text="Sauvegarder"
                    type="submit"
                    icon={FaSave}
                    className="gap-2"
                  />
                  <Button
                    text="Annuler"
                    onClick={handleCloseModal}
                    className="hover:bg-gray-700 gap-2"
                    icon={FaTimes}
                  />
                </div>
                <p className="text-xs text-slate-600">
                  Attention, vous devrez vous déconnecter et reconnecter après
                  les changements.
                </p>
              </form>
            </div>
          </div>
        )}

        {/* Modale photo (URL / Upload + Aperçu + Crop) */}
        {isPhotoModalOpen && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50">
            <div
              className="bg-slate-200 dark:bg-slate-400 p-6 rounded-lg shadow-xl max-w-lg w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-xl font-bold uppercase p-3 mb-4 text-center text-white bg-primary rounded-md shadow">
                Changer la Photo de Profil
              </h2>

              <form onSubmit={handlePhotoSave} className="space-y-4">
                {/* Mode sélection */}
                <div className="flex items-center gap-4">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="photoMode"
                      value="url"
                      checked={uploadMode === "url"}
                      onChange={() => setUploadMode("url")}
                    />
                    <span className="flex items-center gap-2 text-sm">
                      <FaLink /> Lien URL
                    </span>
                  </label>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="photoMode"
                      value="upload"
                      checked={uploadMode === "upload"}
                      onChange={() => setUploadMode("upload")}
                    />
                    <span className="flex items-center gap-2 text-sm">
                      <FaCloudUploadAlt /> Uploader
                    </span>
                  </label>
                </div>

                {/* URL input OU bloc upload */}
                {uploadMode === "url" ? (
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      URL de la photo
                    </label>
                    <input
                      type="text"
                      value={user.photoURL || ""}
                      onChange={(e) => {
                        setUser((p) => ({ ...p, photoURL: e.target.value }));
                        setImgOk(true);
                        setImgVersion((v) => v + 1);
                      }}
                      placeholder="https://… ou /uploads/…"
                      className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2"
                    />
                    <p className="mt-1 text-xs text-slate-600">
                      Utilisez une URL absolue (https://…) ou un chemin interne
                      commençant par /uploads/… (recommandé).
                    </p>
                  </div>
                ) : (
                  <div className="rounded-xl border border-slate-300 p-3 bg-white/80">
                    {/* INPUT FILE caché */}
                    <input
                      ref={fileInputRef}
                      id="user-photo-file"
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                    />

                    <div className="grid grid-cols-1 gap-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          className=" bg-primary rounded-xl gap-2 px-3 py-1.5 text-sm text-white hover:bg-primary-dark flex items-center transition ring-1 ring-primary/70 hover:ring-primary"
                          onClick={openFilePicker}
                        >
                          Choisir un fichier
                        </button>

                        <div className="flex items-center gap-2">
                          <span className="text-sm">Format</span>
                          <select
                            className="select select-bordered select-sm"
                            value={outputFormat}
                            onChange={(e) => setOutputFormat(e.target.value)}
                            disabled={isUploading}
                          >
                            <option value="webp">WEBP (recommandé)</option>
                            <option value="jpg">JPG</option>
                            <option value="png">PNG</option>
                          </select>
                        </div>
                      </div>

                      {isUploading && (
                        <div>
                          <progress
                            className="progress w-full"
                            value={uploadProgress}
                            max="100"
                          />
                          <div className="text-xs mt-1">{uploadProgress}%</div>
                        </div>
                      )}

                      <p className="text-xs opacity-70">
                        L’image sera traitée côté serveur en carré 400×400 et
                        optimisée.
                      </p>
                    </div>
                  </div>
                )}

                {/* Aperçu + Crop */}
                <div>
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <FaImage />
                    <span>Aperçu</span>
                  </div>
                  <div className="mt-2 h-28 w-28 rounded-xl ring-1 ring-slate-300 overflow-hidden bg-slate-100 flex items-center justify-center">
                    <img
                      key={imgVersion}
                      src={
                        user.photoURL
                          ? withBust(
                              toAbsoluteUserPhotoUrl(user.photoURL),
                              imgVersion,
                            )
                          : withBust(DEFAULT_AVATAR, imgVersion)
                      }
                      alt="Aperçu"
                      onError={() => setImgOk(false)}
                      className="h-full w-full object-cover"
                    />
                  </div>

                  {user.photoURL && (
                    <button
                      type="button"
                      className="mt-2 h-10 w-28 rounded-xl ring-1 ring-slate-200 overflow-hidden bg-slate-100 flex items-center justify-center text-xs"
                      onClick={() => setIsCropOpen(true)}
                      disabled={isUploading}
                    >
                      Recadrer / Zoom
                    </button>
                  )}

                  {!imgOk && (
                    <div className="mt-2 text-xs text-rose-600">
                      Impossible de charger l’image (vérifiez l’URL).
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center justify-center gap-3 pt-2">
                  <Button
                    text="Sauvegarder"
                    type="submit"
                    icon={FaSave}
                    className="gap-2"
                  />
                  <Button
                    text="Fermer"
                    onClick={() => setIsPhotoModalOpen(false)}
                    icon={FaTimes}
                    className="gap-2"
                    type="button"
                  />
                </div>
                <div className="border-t border-slate-300">
                  Il faudra peut-être vous déconnecter et reconnecter pour
                  actualiser votre photo partout.
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modale de recadrage (exactement comme adhérent / EditUserModal) */}
        <CropPhotoModal
          isOpen={isCropOpen}
          onClose={() => setIsCropOpen(false)}
          entityId={user?.id}
          imageUrl={
            user.photoURL
              ? `${toAbsoluteUserPhotoUrl(
                  toMasterUrl(user.photoURL),
                )}?v=${imgVersion}`
              : ""
          }
          onCropped={(newUrl) => {
            setUser((p) => ({ ...p, photoURL: newUrl }));
            // maj localStorage pour garder la cohérence globale
            const currentInfo = localStorage.getItem("userInfo");
            const stored = currentInfo ? JSON.parse(currentInfo) : {};
            localStorage.setItem(
              "userInfo",
              JSON.stringify({ ...stored, photoURL: newUrl }),
            );
            setImgVersion((v) => v + 1);
            toast.success("Photo recadrée.");
          }}
          format="webp"
          cropAction={cropUserPhoto}
        />
      </div>

      <ToastContainer />
    </div>
  );
};

export default Profile;
